type GroupMapping = Record<string, string[]>;

export type PathedTreeNode = {
    path?: string;
    is_component?: boolean;
    nodes?: PathedTreeNode[];
    [key: string]: any;
}

export type AllowedComponentPaths = {
    exactPaths: Set<string>;
    wildcardPrefixes: string[];
};

/**
 * Resolves the set of allowed component paths for the given license groups,
 * using the RI Tree group mapping (group name -> component paths).
 * Paths are deduplicated since a component can belong to multiple groups.
 * Supports wildcard entries: paths ending with /* match all components under
 * that prefix, and a bare * matches everything.
 */
export const getAllowedComponentPaths = (
    groupMapping: GroupMapping | undefined | null,
    allowedGroups: string[] | undefined | null
): AllowedComponentPaths => {
    const exactPaths = new Set<string>();
    const wildcardPrefixes: string[] = [];
    if (!groupMapping || !allowedGroups) {
        return { exactPaths, wildcardPrefixes };
    }
    allowedGroups.forEach((groupName) => {
        (groupMapping[groupName] || []).forEach((path) => {
            if (path.endsWith('/*') || path === '*') {
                const prefix = path === '*' ? '' : path.slice(0, -1);
                if (!wildcardPrefixes.includes(prefix)) {
                    wildcardPrefixes.push(prefix);
                }
            } else {
                exactPaths.add(path);
            }
        });
    });
    return { exactPaths, wildcardPrefixes };
};

const isPathAllowed = (componentPath: string, allowed: AllowedComponentPaths): boolean => {
    if (allowed.exactPaths.has(componentPath)) return true;
    return allowed.wildcardPrefixes.some((prefix) => componentPath.startsWith(prefix));
};

/**
 * Filters the RI Tree, keeping only components whose `path` is present in
 * `allowedPaths` or matches a wildcard prefix. Non-component (folder) nodes
 * are kept if at least one of their descendants remains after filtering.
 */
export const filterTreeByGroup = <T extends PathedTreeNode>(
    treeData: T[],
    allowedPaths: AllowedComponentPaths
): T[] => {
    if (!Array.isArray(treeData)) {
        return treeData;
    }
    return treeData.reduce<T[]>((filtered, node) => {
        const nextNode = { ...node };
        if (Array.isArray(nextNode.nodes)) {
            nextNode.nodes = filterTreeByGroup(nextNode.nodes, allowedPaths);
            if (nextNode.nodes.length) {
                filtered.push(nextNode);
            }
            return filtered;
        }
        if (nextNode.is_component) {
            // User custom components are not part of the license group mapping
            // (they have no `path`), so they must always remain visible.
            if (nextNode.isCustomComponent || (nextNode.path && isPathAllowed(nextNode.path, allowedPaths))) {
                filtered.push(nextNode);
            }
            return filtered;
        }
        filtered.push(nextNode);
        return filtered;
    }, []);
};
